import React, { useEffect } from 'react'
import { motion } from 'framer-motion'
import { Search, Dumbbell } from 'lucide-react'

/**
 * "Analyzing your fitness profile..." loading animation.
 * Extracted from PtSubmitRequest's searching state.
 * Fires onComplete after ~3 seconds.
 */
export const PtAnalyzingLoader = ({ onComplete }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            if (onComplete) onComplete()
        }, 3000)
        return () => clearTimeout(timer)
    }, [onComplete])

    return (
        <motion.div
            className="text-center px-4 py-16 min-h-134 flex flex-col items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <div className="relative w-35 h-35 mb-8 flex items-center justify-center">
                <motion.div
                    className="absolute inset-0 rounded-full bg-linear-to-br from-orange-500/30 to-amber-500/30"
                    animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0.1, 0.4] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="absolute inset-3 rounded-full bg-linear-to-br from-orange-500/40 to-amber-500/40"
                    animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.2, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                />
                <motion.div
                    className="relative w-20 h-20 rounded-full bg-linear-to-br from-orange-500 to-amber-500 shadow-xl shadow-orange-500/30 flex items-center justify-center"
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                >
                    <motion.div
                        animate={{ opacity: [1, 0, 0, 1], scale: [1, 0.8, 0.8, 1] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <Search className="w-9 h-9 text-white" />
                    </motion.div>
                    <motion.div
                        className="absolute"
                        animate={{ opacity: [0, 1, 1, 0], scale: [0.8, 1, 1, 0.8] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <Dumbbell className="w-9 h-9 text-white" />
                    </motion.div>
                </motion.div>
                {[...Array(4)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-2 h-2 rounded-full bg-orange-400"
                        style={{
                            top: `${20 + Math.sin(i * 1.5) * 40}%`,
                            left: `${20 + Math.cos(i * 1.5) * 40}%`
                        }}
                        animate={{ y: [0, -15, 0], opacity: [0, 1, 0], scale: [0.5, 1.2, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
                    />
                ))}
            </div>
            <h2 className="text-xl font-bold text-orange-600">Creating your personalized plan...</h2>
            <p className="text-gray-500 mt-2 text-sm">Processing your goals and requirements to find the perfect fit.</p>
        </motion.div>
    )
}
